import { useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useVoiceRecorder from "@/hooks/useVoiceRecorder";
import { api } from "@/lib/api";

/**
 * MicDictate — Reusable microphone button that records a short clip, uploads
 * it to /api/stt/transcribe, and hands the transcribed text back to the parent
 * via `onTranscript`. Works on desktop AND mobile browsers (iOS/Android).
 *
 * Props:
 *  - onTranscript(text):   called when transcription returns successfully.
 *                          Parent decides whether to replace or append.
 *  - context:              short prompt string (e.g. "stock take note") that
 *                          nudges Whisper toward the domain vocabulary.
 *  - language:             "" | "en" | ISO code. "" = auto-detect.
 *  - maxSeconds:           hard-cap on recording length (default 180s).
 *  - size:                 "sm" | "md"  — visual size of the button.
 *  - testId:               data-testid for E2E tests.
 */
export default function MicDictate({
  onTranscript,
  context = "",
  language = "",
  maxSeconds = 180,
  size = "sm",
  testId = "mic-dictate-button",
  disabled = false,
}) {
  const rec = useVoiceRecorder({ maxSeconds });
  const [sending, setSending] = useState(false);

  const sizePx = size === "md" ? "h-10 w-10" : "h-8 w-8";
  const iconPx = size === "md" ? "h-5 w-5" : "h-4 w-4";

  const handleClick = async () => {
    if (sending) return;
    if (!rec.supported) {
      toast.error("Microphone isn't available in this browser.");
      return;
    }
    if (rec.recording) {
      // Stop + upload
      const { blob, mimeType } = await rec.stop();
      if (!blob || blob.size === 0) return;
      setSending(true);
      try {
        const form = new FormData();
        const ext = (mimeType || "audio/webm").includes("mp4")
          ? "m4a"
          : (mimeType || "").includes("wav")
          ? "wav"
          : "webm";
        form.append("audio", blob, `clip.${ext}`);
        if (language) form.append("language", language);
        if (context) form.append("context", context);
        const resp = await api.post("/stt/transcribe", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const text = resp.data?.text?.trim();
        if (!text) {
          toast.message("Nothing was captured. Try again a little closer to the mic.");
          return;
        }
        onTranscript?.(text);
        toast.success(`Transcribed (${resp.data?.language || "auto"})`);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Transcription failed. Try again.";
        toast.error(msg);
      } finally {
        setSending(false);
      }
    } else {
      const ok = await rec.start();
      if (!ok && rec.error) toast.error(rec.error);
    }
  };

  const label = rec.recording
    ? `Recording ${rec.elapsed}s — tap to stop`
    : sending
    ? "Transcribing…"
    : "Dictate";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || sending}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`${sizePx} inline-flex items-center justify-center rounded-full border transition-all ${
        rec.recording
          ? "border-red-500 bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/40"
          : sending
          ? "border-zinc-300 bg-zinc-100 text-zinc-500"
          : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {sending ? (
        <Loader2 className={`${iconPx} animate-spin`} />
      ) : rec.recording ? (
        <Square className={`${iconPx}`} fill="currentColor" />
      ) : (
        <Mic className={`${iconPx}`} />
      )}
    </button>
  );
}
